import React from 'react';
import { path, pathOr } from 'ramda';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';

import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import { getExchangePaymentURL } from 'utils/urls';
import { hasHotel } from 'utils/booking';
import { useCurrencyFromURL } from 'utils/hooks/currency';
import { rollbackVoucherPayments } from 'components/ui/Payments/Payments.utils';
import { historyShape } from 'constants/propTypes';

import { ROLLBACK_VOUCHER_REDEMPTION, CANCEL_PAYMENTS } from 'store/mutations';
import PaymentExchange from './PaymentExchange';
import { GET_BOOKING, GET_USER } from './graphql';
import { GET_CUSTOMER_DETAILS } from 'store/queries';

const PaymentExchangeContainer = ({ match, history: { location } }) => {
  const client = useApolloClient();

  const membershipNumber = new URLSearchParams(location?.search).get('membershipNumber');

  const { reference } = match.params;

  const userState = useQuery(GET_USER);

  const user = pathOr([], ['data', 'me'], userState);

  const [currency] = useCurrencyFromURL(user.paymentCurrencies);

  const bookingState = useQuery(GET_BOOKING, {
    variables: { reference, currency },
    fetchPolicy: 'no-cache'
  });

  const booking = path(['data', 'booking'], bookingState);
  const isUpgradeWithPointsPath = new URLSearchParams(location?.search).get('isUpgradeWithPoints');

  const [cancelPaymentsMutation, cancelPaymentsState] = useMutation(CANCEL_PAYMENTS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [rollbackVoucherRedemption, rollbackVoucherRedemptionState] = useMutation(ROLLBACK_VOUCHER_REDEMPTION);

  const cancelPayments = async () => {
    let customerInfo;

    if (!isUpgradeWithPointsPath && membershipNumber) {
      try {
        const result = await client.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });

        customerInfo = result?.data?.getCustomerDetails;
      } catch (e) {
        console.error('Failed to get customer details:', e);
      }
    }

    return Promise.all([
      cancelPaymentsMutation({
        variables: {
          bookingReference: reference,
          currency,
          paymentLocation: user.paymentLocation.name,
          hasHotel: booking?.isPackage || hasHotel(booking),
          customerId: customerInfo?.profile?.customerId,
          shouldCancelLoyalty: !isUpgradeWithPointsPath
        }
      }),
      rollbackVoucherPayments({
        payments: pathOr([], ['data', 'booking', 'payments'], bookingState),
        bookingReference: reference,
        currency,
        rollbackMutation: rollbackVoucherRedemption
      })
    ]);
  };
  const state = [userState, bookingState, cancelPaymentsState, rollbackVoucherRedemptionState];
  const error = state.map((item) => item.error).find(Boolean);
  const loading = state.some((item) => item.loading);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getExchangePaymentURL(reference, currency)
    });
  }

  return (
    <PaymentExchange
      currency={currency}
      loading={loading}
      booking={booking}
      refetchBooking={bookingState.refetch}
      cancelPayments={cancelPayments}
    />
  );
};

PaymentExchangeContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      reference: PropTypes.string.isRequired
    })
  }),
  history: historyShape
};

export default PaymentExchangeContainer;
